<template>
  <div class="safari-calendar">
    <app-cells>
      <div v-if="switchable" class="safari-calendar__mode-switch" role="group" aria-label="Calendar view">
        <button
          type="button"
          :class="{ 'safari-calendar__mode-button--active': isCarCalendar }"
          :aria-pressed="isCarCalendar ? 'true' : 'false'"
          @click="setCalendarMode('car')"
        >
          <span>Car</span>
          <span v-if="isCarCalendar && totalConflictCount" class="safari-calendar__mode-conflict-count">{{ totalConflictCount }}</span>
        </button>
        <button
          type="button"
          :class="{ 'safari-calendar__mode-button--active': isGuideCalendar }"
          :aria-pressed="isGuideCalendar ? 'true' : 'false'"
          @click="setCalendarMode('guide')"
        >
          <span>Guide</span>
          <span v-if="isGuideCalendar && totalConflictCount" class="safari-calendar__mode-conflict-count">{{ totalConflictCount }}</span>
        </button>
      </div>
      <div class="date-picker-wrapper">
        <VueCtkDateTimePicker
          id="date_picker_balance"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates"
          :custom-shortcuts="custom_shortcuts"
          shortcut="next30Days"
          @is-hidden="onIsHiddenPicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Select dates"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          noClearButton
        />
        <div v-if="date_picker_error" class="date-picker-wrapper__error">Choose an end date</div>
      </div>
      <app-button @click.native="onClickRefreshButton" ref="refresh">Refresh</app-button>
      <app-button v-if="!is_sales_read_only" @click.native="onClickShareButton" ref="share" theme="info">Share Schedule</app-button>
      <app-button v-if="can_manage_schedule_blocks" @click.native="openBlockModal">{{ availabilityActionLabel }}</app-button>
      <details class="safari-calendar__legend">
        <summary class="safari-calendar__legend-button">
          <img src="@/assets/img/hm/legend.svg" alt="">
          <span>Legend</span>
          <i aria-hidden="true"></i>
        </summary>
        <div class="safari-calendar__legend-panel">
          <section>
            <strong>Status</strong>
            <ul>
              <li>
                <i class="safari-calendar__legend-status safari-calendar__status--request"></i>
                Request in process (pending car)
              </li>
              <li>
                <i class="safari-calendar__legend-status safari-calendar__status--provisional"></i>
                Booked provisionally
              </li>
              <li>
                <i class="safari-calendar__legend-status safari-calendar__status--pending"></i>
                Group - pending assignment
              </li>
              <li>
                <i class="safari-calendar__legend-status safari-calendar__status--confirmed"></i>
                Group - confirmed
              </li>
            </ul>
          </section>
          <section>
            <strong>Availability</strong>
            <ul>
              <li v-if="isCarCalendar"><i class="safari-calendar__legend-availability safari-calendar__legend-availability--maintenance"></i> Maintenance</li>
              <li v-else><i class="safari-calendar__legend-availability safari-calendar__legend-availability--training"></i> Training</li>
              <li v-if="isGuideCalendar"><i class="safari-calendar__legend-guide-leave"></i> Leave</li>
              <li><span class="safari-calendar__legend-conflict"><img src="@/assets/img/warning-round-icon.svg" alt=""></span> Conflicts with a booking</li>
              <li><span class="safari-calendar__legend-lock"></span> {{ isGuideCalendar ? 'Locked guide assignment' : 'Locked car assignment' }}</li>
            </ul>
          </section>
          <section>
            <strong>Group tags</strong>
            <ul>
              <li><span>🧸</span> Children in group</li>
              <li><span>🦩</span> Birdwatchers</li>
              <li><span>🛬</span> Fly-in</li>
              <li><span>🛫</span> Fly-out</li>
              <li><i :class="['fflag', 'ff-sm', `fflag-${languageFlagCountry.FR}`]"/> Group language</li>
              <li><span class="safari-calendar__legend-senior">60+</span> Guests aged 60+</li>
              <li><span>♿</span> Accessibility needs</li>
              <li><span>💍</span> Honeymoon</li>
            </ul>
          </section>
        </div>
      </details>
    </app-cells>

    <app-spinner v-if="is_initial_loading" />
    <template v-else>
      <div v-if="is_refreshing" class="safari-calendar__loading">Updating calendar...</div>
      <div v-if="availability_error && isCarCalendar" class="safari-calendar__availability-warning">
        Vehicle maintenance could not be loaded. Booking data is still up to date.
      </div>
      <app-no-results v-if="!displayVehicles.length" :text="emptyCalendarText" />
      <div ref="calendar_scroll" v-else class="safari-calendar__scroll" :class="{ 'safari-calendar__scroll--dragging': is_calendar_dragging }" :style="{ userSelect: is_calendar_dragging ? 'none' : '' }" @dragstart.prevent>
        <div class="safari-calendar__grid" :style="gridStyle">
          <div class="safari-calendar__corner">{{ calendarCornerLabel }}</div>
          <div
            v-for="day in calendar.days"
            :key="day.date"
            class="safari-calendar__day"
            :class="{ 'safari-calendar__day--today': day.is_today, 'safari-calendar__day--selected': isDaySelected(day.date) }"
            @mousedown="startCalendarDrag"
            @click="onDayClick(day.date)"
          >
            <span>{{ day.label }}</span>
            <small>{{ day.weekday }}</small>
          </div>

          <template v-for="(vehicle, vehicleIndex) in displayVehicles">
            <div
              :key="`${vehicle.id}-vehicle`"
              class="safari-calendar__vehicle"
              :class="{
                'safari-calendar__vehicle--conflict': hasVehicleConflict(vehicle.id),
                'safari-calendar__vehicle--selected': isVehicleSelected(vehicle.id)
              }"
              :style="{ gridRow: vehicleIndex + 2 }"
              @click="toggleVehicle(vehicle.id)"
            >
              <strong class="safari-calendar__vehicle-name" :title="resourcePrimaryLabel(vehicle)">{{ resourcePrimaryLabel(vehicle) }}</strong>
              <div v-if="switchable || isCarCalendar" class="safari-calendar__guide-row">
                <span v-if="isCarCalendar" class="safari-calendar__driver" :title="vehicle.current_driver && driverName(vehicle.current_driver)">{{ vehicle.current_driver ? driverName(vehicle.current_driver) : 'No guide assigned' }}</span>
                <span v-else class="safari-calendar__driver">{{ bookedDaysLabel(vehicle.days_booked) }}</span>
                <div v-if="vehicle.current_driver && hasGuideTags(vehicle.current_driver) && ((switchable && isGuideCalendar) || (!switchable && isCarCalendar))" class="safari-calendar__guide-tags">
                  <span
                    v-for="language in vehicle.current_driver.languages"
                    :key="language.code"
                    v-tooltip="{ content: guideLanguageTooltip(language), placement: 'top', offset: 8 }"
                  >
                    <i
                      v-if="languageFlagCode(language)"
                      :class="[
                        'fflag',
                        'ff-sm',
                        `fflag-${languageFlagCode(language)}`
                      ]"
                    />
                    <template v-else>{{ language.code.toUpperCase() }}</template>
                  </span>
                  <span v-if="vehicle.current_driver.strong_in_birds" v-tooltip="{ content: 'Birdwatching expert', placement: 'top', offset: 8 }">🦩</span>
                  <span v-if="vehicle.current_driver.great_with_kids" v-tooltip="{ content: 'Great with kids', placement: 'top', offset: 8 }">🧸</span>
                  <span v-if="vehicle.current_driver.influencer_experienced" v-tooltip="{ content: 'Influencer-experienced', placement: 'top', offset: 8 }">📸</span>
                </div>
              </div>
            </div>
            <div
              v-for="day in calendar.days"
              :key="`${vehicle.id}-${day.date}`"
              class="safari-calendar__cell"
              :class="{
                'safari-calendar__cell--conflict': hasVehicleConflict(vehicle.id),
                'safari-calendar__cell--selected': isVehicleSelected(vehicle.id) || isDaySelected(day.date)
              }"
              :style="{ gridColumn: dayColumn(day.date), gridRow: vehicleIndex + 2 }"
            ></div>
            <button
              v-for="booking in bookingsForVehicle(vehicle.id)"
              :key="booking.id"
              type="button"
              class="safari-calendar__booking"
              :class="[bookingStatusClass(booking), {
                'safari-calendar__booking--conflict': isBookingConflict(booking.id),
                'safari-calendar__booking--locked': isBookingAssignmentLocked(booking)
              }]"
              :style="bookingStyle(booking, vehicleIndex)"
              @click="onBookingClick(booking)"
            >
              <span
                class="safari-calendar__booking-title"
                v-tooltip="{ content: booking.title, placement: 'top', offset: 8 }"
              >{{ booking.title }}</span>
              <span class="safari-calendar__markers">
                <span v-if="isBookingConflict(booking.id)" class="safari-calendar__booking-warning" aria-label="Schedule conflict" v-tooltip="{ content: 'Schedule conflict', placement: 'top', offset: 8 }"><img src="@/assets/img/warning-round-icon.svg" alt=""></span>
                <span
                  v-for="marker in compactMarkers(booking)"
                  :key="marker.key"
                  v-tooltip="{ content: marker.tooltip || marker.detailValue, placement: 'top', offset: 8 }"
                  :class="{ 'safari-calendar__marker--senior': marker.key === 'seniors', 'safari-calendar__marker--more': marker.key === 'more' }"
                >
                  <i v-if="marker.flag_country_code" :class="['fflag', 'ff-sm', `fflag-${marker.flag_country_code}`]"/>
                  <template v-else>{{ marker.label }}</template>
                </span>
              </span>
            </button>
            <button
              v-for="block in availabilityBlocksForVehicle(vehicle.id)"
              :key="block.id"
              type="button"
              class="safari-calendar__block"
              :class="{ 'safari-calendar__block--conflict': block.conflicting_booking_ids.length, 'safari-calendar__block--training': isGuideCalendar }"
              :style="blockStyle(block, vehicleIndex)"
              :aria-label="block.description ? `${availabilityBlockLabel}: ${block.description}` : availabilityBlockLabel"
              :title="block.description || availabilityBlockLabel"
              @click="openBlockDetail(block)"
            >
              <span class="safari-calendar__block-icon" aria-hidden="true">{{ isGuideCalendar ? '🎓' : '🔧' }}</span>
              <span class="safari-calendar__block-label">{{ availabilityBlockLabel }}</span>
            </button>
            <button
              v-for="leave in leavesForVehicle(vehicle.id)"
              :key="leave.id"
              type="button"
              class="safari-calendar__leave"
              :style="blockStyle(leave, vehicleIndex)"
              :title="leave.leave_type.name"
              @click="openLeaveDetail(leave)"
            >
              <span>Leave</span>
            </button>
          </template>
        </div>
      </div>
    </template>

    <modal name="share-popup" :width="400" :adaptive="true" :clickToClose="false" :height="'auto'" :reset="true" :scrollable="true" class="modal-not-overflow" draggable=".popup__title">
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('share-popup')"></button>
        <div class="popup__title">Share driver's schedule</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckShareModalForm">
            <app-form-group label="Date" required>
              <app-date-picker v-model="share_form.date" placeholder="Date" range :clearable="false" :error="$v.share_form.date.$error"/>
              <template #error><div v-if="$v.share_form.date.$dirty && !$v.share_form.date.required">Required field</div></template>
            </app-form-group>
            <app-form-group label="Drivers">
              <template #additional>
                <button v-show="safari_driver_options.length !== share_form.drivers.length" @click="onAddAllDrivers" type="button" style="line-height: 0"><img src="@/assets/img/add-all-icon.svg" alt=""></button>
                <button v-show="share_form.drivers.length" @click="onRemoveAllDrivers" type="button" style="line-height: 0"><img src="@/assets/img/clear-all-icon.svg" alt=""></button>
              </template>
              <app-select v-model="share_form.drivers" :options="safari_driver_options" label="full_name" multiple placeholder="Driver"/>
            </app-form-group>
            <app-cells :indent="false" position="center"><app-button ref="submit_share" :disabled="$v.share_form.$error" type="submit">Send</app-button></app-cells>
          </form>
        </div>
      </div>
    </modal>

    <modal name="schedule-block" :width="440" :adaptive="true" :clickToClose="false" :height="'auto'" :scrollable="true" class="modal-not-overflow">
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('schedule-block')"></button>
        <div class="popup__title">Add {{ availabilityBlockLabel.toLowerCase() }}</div>
        <div class="popup__body">
          <app-form-group :label="isGuideCalendar ? 'Guide' : 'Vehicle'" required>
            <app-select v-model="block_form.resource" :options="block_resource_options" label="name" :clearable="false" />
          </app-form-group>
          <app-form-group label="From date" required><app-date-picker v-model="block_form.from_date" placeholder="From date" /></app-form-group>
          <app-form-group label="To date" required><app-date-picker v-model="block_form.to_date" placeholder="To date" /></app-form-group>
          <app-form-group label="Description"><app-textarea v-model="block_form.description" placeholder="Description" /></app-form-group>
          <app-cells :indent="false" position="center">
            <app-button ref="blockSubmit" @click.native="createBlock">Add {{ availabilityBlockLabel.toLowerCase() }}</app-button>
          </app-cells>
        </div>
      </div>
    </modal>

    <modal name="schedule-block-detail" :width="420" :adaptive="true" :clickToClose="false" :height="'auto'" class="modal-not-overflow">
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('schedule-block-detail')"></button>
        <div class="popup__title">{{ availabilityBlockLabel }}</div>
        <div v-if="selected_block" class="popup__body safari-block-detail">
          <strong>{{ selectedBlockResourceName }}</strong>
          <span>{{ formatDate(selected_block.from_date) }}–{{ formatDate(selected_block.to_date) }}</span>
          <p v-if="selected_block.description">{{ selected_block.description }}</p>
          <div v-if="selectedAffectedBookings.length" class="safari-block-detail__bookings">
            <strong>Affected bookings</strong>
            <button v-for="booking in selectedAffectedBookings" :key="booking.id" type="button" @click="openAffectedBooking(booking)">
              {{ booking.title }} · {{ formatDate(booking.date_start) }}–{{ formatDate(booking.date_end) }}
            </button>
          </div>
          <app-cells v-if="can_manage_schedule_blocks" :indent="false" position="center">
            <app-button ref="blockCancel" theme="error" @click.native="cancelBlock(selected_block)">Cancel {{ availabilityBlockLabel.toLowerCase() }}</app-button>
          </app-cells>
        </div>
      </div>
    </modal>

    <modal name="guide-leave-detail" :width="420" :adaptive="true" :clickToClose="false" :height="'auto'" class="modal-not-overflow">
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('guide-leave-detail')"></button>
        <div class="popup__title">Guide leave</div>
        <div v-if="selected_leave" class="popup__body safari-block-detail">
          <strong>{{ selectedLeaveGuideName }}</strong>
          <span>{{ selected_leave.leave_type.name }}</span>
          <span>{{ formatDate(selected_leave.actual_from_date) }}–{{ formatDate(selected_leave.actual_to_date) }}</span>
        </div>
      </div>
    </modal>

    <modal name="booking-details" :width="1120" :adaptive="true" :clickToClose="false" :height="'auto'" :reset="true" :scrollable="true" class="modal-not-overflow" draggable=".popup__title" @closed="resetBookingDetails">
      <div class="popup safari-booking-modal">
        <button class="popup__close" type="button" @click="$modal.hide('booking-details')"></button>
        <div class="popup__title">Safari booking</div>
        <div v-if="is_booking_loading" class="popup__body safari-booking-modal__loading"><app-spinner /></div>
        <div v-else-if="booking_detail_error" class="popup__body"><app-no-results text="Booking details could not be loaded" /></div>
        <div v-else-if="detail.reservation" class="popup__body safari-booking-modal__body" :class="{ 'safari-booking-modal__body--with-itinerary': detailHasTimeline }">
          <div>
            <div class="safari-booking-modal__heading">
              <div><span>Safari reservation</span><h2>{{ detail.reservation.title }}</h2></div>
              <b>{{ detail.reservation.reservation_status.name }}</b>
            </div>
            <div class="safari-booking-modal__dates"><div><span>Start</span><strong>{{ formatDate(detail.dates.tour_start) }}</strong></div><span>to</span><div><span>End</span><strong>{{ formatDate(detail.dates.tour_end) }}</strong></div><b>{{ duration(detail.dates) }} days</b></div>
            <div class="safari-booking-modal__facts"><div><span>Vehicle</span><strong>{{ vehicleLabel(detail.vehicle) }}</strong></div><div><span>Assigned guide</span><strong>{{ detail.assigned_driver ? driverName(detail.assigned_driver) : 'Not assigned' }}</strong></div></div>
            <div v-if="hasDetailAssignmentLocks" class="safari-booking-modal__section safari-booking-modal__locks">
              <span>Assignment locks</span>
              <div>
                <b v-if="detail.assignment_locks.car_assignment_locked">🔒 Car assignment</b>
                <b v-if="detail.assignment_locks.guide_assignment_locked">🔒 Guide assignment</b>
              </div>
            </div>
            <div v-if="detail.guide_leave_conflicts && detail.guide_leave_conflicts.length" class="safari-booking-modal__guide-leave">
              <strong>Assigned guide is on leave</strong>
              <div v-for="leave in detail.guide_leave_conflicts" :key="leave.id">
                <span>{{ leave.leave_type.name }}: {{ formatDate(leave.from_date) }}–{{ formatDate(leave.to_date) }}</span>
                <b>Overlaps this booking: {{ formatDate(leave.overlap_from) }}–{{ formatDate(leave.overlap_to) }} · {{ leave.overlap_days }} day{{ leave.overlap_days === 1 ? '' : 's' }}</b>
              </div>
            </div>
            <div v-if="detail.route" class="safari-booking-modal__facts safari-booking-modal__facts--route"><div><span>Start point</span><strong>{{ locationName(detail.route.start_point) }}</strong></div><div><span>Finish point</span><strong>{{ locationName(detail.route.finish_point) }}</strong></div></div>
            <div v-if="detail.assigned_driver && detail.assigned_driver.languages.length" class="safari-booking-modal__section"><span>Guide languages</span><div class="safari-booking-modal__chips"><b v-for="language in detail.assigned_driver.languages" :key="language.code">{{ language.name }} {{ language.level_name }}</b></div></div>
            <div v-if="detail.assigned_driver && hasGuideSkills(detail.assigned_driver)" class="safari-booking-modal__section"><span>Guide strengths</span><div class="safari-booking-modal__chips safari-booking-modal__chips--plain"><b v-if="detail.assigned_driver.strong_in_birds">🦩 Birdwatching expert</b><b v-if="detail.assigned_driver.great_with_kids">🧸 Great with kids</b><b v-if="detail.assigned_driver.influencer_experienced">📸 Influencer-experienced</b></div></div>
            <div class="safari-booking-modal__section"><span>Group profile and requests</span><div v-if="detailMarkers.length" class="safari-booking-modal__markers"><div v-for="marker in detailMarkers" :key="marker.key"><b><i v-if="marker.flag_country_code" :class="['fflag', 'ff-sm', `fflag-${marker.flag_country_code}`]"/><template v-else>{{ marker.label }}</template></b><strong>{{ marker.value }}</strong></div></div><p v-else>No special group markers</p></div>
            <div v-if="detailClients.length" class="safari-booking-modal__section">
              <span>Clients</span>
              <table class="safari-booking-modal__clients">
                <thead><tr><th>First name</th><th>Age at tour</th><th>Category</th></tr></thead>
                <tbody>
                  <tr v-for="client in detailClients" :key="client.id">
                    <td>{{ client.first_name }} <span v-if="client.birthday_during_tour" :title="birthdayTooltip(client)">🎂</span></td>
                    <td>{{ client.age === null ? 'Unknown' : `${client.age}yo` }}</td>
                    <td><span :title="client.categoryTitle">{{ client.categoryMarker }}</span> {{ client.categoryTitle }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="detail.reservation.notes" class="safari-booking-modal__section"><span>Reservation notes</span><p>{{ detail.reservation.notes }}</p></div>
            <app-cells :indent="false" :margin-top="12" position="end"><app-button @click.native="openReservation">Open reservation</app-button></app-cells>
          </div>
          <div v-if="detailHasTimeline" class="safari-booking-modal__itinerary">
            <span>Itinerary</span>
            <div v-for="item in detailTimeline" :key="item.key">
              <strong>{{ formatDate(item.date) }}: {{ item.label }}</strong>
              <small v-if="item.time">{{ item.time }}</small>
            </div>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import moment from 'moment'
import { getEmployeesList, getReservationsSafariScheduleAvailability, getReservationsSafariScheduleCalendarBooking, getReservationsSafariScheduleCalendarV2, getReservationsSafariScheduleCombinedCalendarV2, getReservationsSafariScheduleGuideCalendarV2, getReservationsSafariScheduleGuidePaidLeaves, postReservationsSafariDriverSchedule, postReservationsSafariScheduleBlock, postReservationsSafariScheduleBlockCancel } from '@/http'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'SafariScheduleCalendar',
  components: { VueCtkDateTimePicker },
  props: {
    mode: {
      type: String,
      default: 'car',
      validator: value => ['car', 'guide'].includes(value),
    },
    switchable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const start = moment().format('YYYY-MM-DD')
    const end = moment().add(30, 'days').format('YYYY-MM-DD')
    return {
      is_initial_loading: true,
      calendar_request_id: 0,
      is_refreshing: false,
      filter_dates: { start, end },
      date_picker_error: false,
      custom_shortcuts: [
        { key: 'next30Days', label: 'Next 30 days', value: () => ({ start: moment(), end: moment().add(30, 'days') }) },
        { key: 'next60Days', label: 'Next 60 days', value: () => ({ start: moment(), end: moment().add(60, 'days') }) },
        { key: 'next90Days', label: 'Next 90 days', value: () => ({ start: moment(), end: moment().add(90, 'days') }) },
        { key: 'next180Days', label: 'Next 180 days', value: () => ({ start: moment(), end: moment().add(180, 'days') }) },
        { key: 'next365Days', label: 'Next 365 days', value: () => ({ start: moment(), end: moment().add(365, 'days') }) },
      ],
      calendar: { days: [], vehicles: [], bookings: [] },
      calendar_modes: { car: null, guide: null },
      active_mode: this.mode,
      availability: { vehicle_blocks: [], training_blocks: [], leaves: [] },
      availability_error: false,
      languageFlagCountry: {
        FR: 'FR',
        fr: 'FR',
        DE: 'DE',
        de: 'DE',
        IT: 'IT',
        it: 'IT',
        ES: 'ES',
        es: 'ES',
      },
      safari_driver_options: [],
      share_form: { date: null, drivers: [] },
      is_booking_loading: false,
      booking_detail_error: false,
      detail: {},
      is_calendar_pointer_down: false,
      is_calendar_dragging: false,
      calendar_drag_start_x: 0,
      calendar_drag_start_scroll_left: 0,
      selected_vehicle_ids: [],
      selected_dates: [],
      block_form: { resource: null, from_date: start, to_date: start, description: '' },
      selected_block: null,
      selected_leave: null,
    }
  },
  validations: { share_form: { date: { required } } },
  computed: {
    isCarCalendar() { return this.active_mode === 'car' },
    isGuideCalendar() { return this.active_mode === 'guide' },
    availabilityActionLabel() { return this.isGuideCalendar ? 'Add training' : 'Add maintenance' },
    availabilityBlockLabel() { return this.isGuideCalendar ? 'Training' : 'Maintenance' },
    calendarCornerLabel() { return this.isGuideCalendar ? 'Guide' : 'Car / assigned guide' },
    emptyCalendarText() { return this.isGuideCalendar ? 'There are no guides in this period' : 'There are no vehicles in this period' },
    displayVehicles() {
      if (!this.switchable || this.isCarCalendar) return this.calendar.vehicles
      const carCalendar = this.calendar_modes.car
      if (!carCalendar) return this.calendar.vehicles

      const guidesByEmployeeId = this.calendar.vehicles.reduce((result, guide) => {
        result[guide.employee_id] = guide
        return result
      }, {})
      const assignedGuideIds = new Set()
      const assignedGuides = carCalendar.vehicles.reduce((result, vehicle) => {
        const employeeId = vehicle.current_driver && vehicle.current_driver.id
        const guide = guidesByEmployeeId[employeeId]
        if (!guide || assignedGuideIds.has(employeeId)) return result
        assignedGuideIds.add(employeeId)
        result.push(guide)
        return result
      }, [])
      const guidesWithoutCars = this.calendar.vehicles.filter(guide => !assignedGuideIds.has(guide.employee_id))
      return [...assignedGuides, ...guidesWithoutCars]
    },
    gridStyle() {
      const rows = this.displayVehicles
        .map(vehicle => `minmax(${this.vehicleRowHeight(vehicle.id)}px, auto)`)
        .join(' ')
      return {
        gridTemplateColumns: `215px repeat(${this.calendar.days.length}, 78px)`,
        gridTemplateRows: `minmax(46px, auto) ${rows}`,
      }
    },
    detailMarkers() {
      return this.allMarkers(this.detail.markers || {}).map(marker => ({ ...marker, value: marker.detailValue }))
    },
    detailTimeline() {
      const itinerary = (this.detail.itinerary || []).map((item, index) => ({
        key: `itinerary-${item.date}-${index}`,
        date: item.date,
        label: item.national_park || this.humanizeCode(item.type),
        time: null,
        sortOrder: 0,
      }))
      const flights = (this.detail.domestic_flights || []).map((item, index) => ({
        key: `flight-${item.flight_id}-${item.journey_id || index}`,
        date: item.date,
        label: `✈️ ${this.flightLocationName(item.origin)} → ${this.flightLocationName(item.destination)}`,
        time: [item.departure, item.arrival].filter(Boolean).join(' - '),
        sortOrder: 1,
      }))
      return [...itinerary, ...flights].sort((first, second) => {
        const dateDiff = moment(first.date).diff(moment(second.date))
        const typeDiff = first.sortOrder - second.sortOrder
        return dateDiff || typeDiff || (first.time || '').localeCompare(second.time || '') || first.key.localeCompare(second.key)
      })
    },
    detailHasTimeline() {
      return this.detailTimeline.length > 0
    },
    hasDetailAssignmentLocks() {
      const locks = this.detail.assignment_locks || {}
      return Boolean(locks.car_assignment_locked || locks.guide_assignment_locked)
    },
    detailClients() {
      const clients = this.detail.clients || {}
      const categories = [
        { key: 'children', marker: '🧸', title: 'Child' },
        { key: 'adults', marker: '●', title: 'Adult' },
        { key: 'seniors', marker: '60+', title: 'Senior' },
        { key: 'unknown_age', marker: '?', title: 'Age unknown' },
      ]
      return categories.flatMap(category => (clients[category.key] || []).map(client => ({
        ...client,
        categoryMarker: category.marker,
        categoryTitle: category.title,
      })))
    },
    is_sales_read_only() {
      const groups = this.$store.state.userGroups.map(item => item.code)
      return groups.includes('sales_manager') || groups.includes('sales_supervisor')
    },
    can_manage_schedule_blocks() {
      const groups = this.$store.state.userGroups.map(item => item.code)
      return ['general_manager', 'safari_manager', 'safari_supervisor'].some(code => groups.includes(code))
    },
    block_resource_options() {
      if (this.isGuideCalendar) return this.displayVehicles
      return this.displayVehicles.filter(vehicle => vehicle.type === 'own')
    },
    availabilityConflictCount() {
      const displayedVehicleIds = this.displayVehicles.map(vehicle => vehicle.id)
      return this.availabilityBlocks.filter(event => displayedVehicleIds.includes(event.vehicle_id) && (event.conflicting_booking_ids || []).length).length
    },
    availabilityBlocks() { return this.isGuideCalendar ? this.availability.training_blocks : this.availability.vehicle_blocks },
    calendarItemLayout() {
      const result = {}
      this.displayVehicles.forEach(vehicle => {
        const trackEnds = []
        const items = [
          ...this.bookingsForVehicle(vehicle.id).map(booking => ({
            id: booking.id,
            from_date: booking.date_start,
            to_date: booking.date_end,
            priority: 0,
          })),
          ...this.availabilityBlocksForVehicle(vehicle.id).map(block => ({
            id: block.id,
            from_date: block.from_date,
            to_date: block.to_date,
            priority: 1,
          })),
          ...this.leavesForVehicle(vehicle.id).map(leave => ({
            id: leave.id,
            from_date: leave.from_date,
            to_date: leave.to_date,
            priority: 2,
          })),
        ].sort((first, second) => first.from_date.localeCompare(second.from_date) || first.priority - second.priority || first.to_date.localeCompare(second.to_date) || first.id.localeCompare(second.id))
        items.forEach(item => {
          let track = trackEnds.findIndex(endDate => endDate < item.from_date)
          if (track === -1) track = trackEnds.length
          trackEnds[track] = item.to_date
          result[item.id] = { track, track_count: trackEnds.length }
        })
        items.forEach(item => { result[item.id].track_count = trackEnds.length })
      })
      return result
    },
    bookingOverlapPairs() {
      const pairs = []
      this.displayVehicles.forEach(vehicle => {
        const bookings = this.bookingsForVehicle(vehicle.id)
        bookings.forEach((first, index) => {
          bookings.slice(index + 1).forEach(second => {
            if (first.date_start <= second.date_end && first.date_end >= second.date_start) pairs.push([first.id, second.id])
          })
        })
      })
      return pairs
    },
    bookingConflictIds() {
      const ids = this.bookingOverlapPairs.flat()
      this.availabilityBlocks.forEach(event => ids.push(...event.conflicting_booking_ids))
      return [...new Set(ids)]
    },
    totalConflictCount() {
      return this.bookingOverlapPairs.length + this.availabilityConflictCount
    },
    selectedBlockResourceName() {
      if (!this.selected_block) return ''
      const vehicle = this.calendar.vehicles.find(item => item.id === this.selected_block.vehicle_id)
      return vehicle ? vehicle.name : (this.isGuideCalendar ? 'Guide' : 'Vehicle')
    },
    selectedLeaveGuideName() {
      if (!this.selected_leave) return ''
      const guide = this.calendar.vehicles.find(item => item.id === this.selected_leave.vehicle_id)
      return guide ? guide.name : 'Guide'
    },
    selectedAffectedBookings() {
      if (!this.selected_block) return []
      return this.selected_block.conflicting_booking_ids
        .map(id => this.calendar.bookings.find(booking => booking.id === id))
        .filter(Boolean)
    },
  },
  mounted() {
    this.fetchItems()
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onCalendarDrag)
    document.removeEventListener('mouseup', this.endCalendarDrag)
  },
  methods: {
    isBookingAssignmentLocked(booking) {
      const locks = booking.assignment_locks || {}
      return this.isGuideCalendar ? Boolean(locks.guide_assignment_locked) : Boolean(locks.car_assignment_locked)
    },
    startCalendarDrag(event) {
      if (event.button !== 0 || !this.$refs.calendar_scroll) return
      this.is_calendar_pointer_down = true
      this.is_calendar_dragging = false
      this.calendar_drag_start_x = event.pageX
      this.calendar_drag_start_scroll_left = this.$refs.calendar_scroll.scrollLeft
      document.addEventListener('mousemove', this.onCalendarDrag)
      document.addEventListener('mouseup', this.endCalendarDrag)
    },
    onCalendarDrag(event) {
      if (!this.is_calendar_pointer_down) return
      const distance = event.pageX - this.calendar_drag_start_x
      if (!this.is_calendar_dragging && Math.abs(distance) > 5) this.is_calendar_dragging = true
      if (this.is_calendar_dragging) {
        this.$refs.calendar_scroll.scrollLeft = this.calendar_drag_start_scroll_left - distance
        event.preventDefault()
      }
    },
    endCalendarDrag() {
      if (!this.is_calendar_pointer_down) return
      this.is_calendar_pointer_down = false
      setTimeout(() => { this.is_calendar_dragging = false }, 0)
      document.removeEventListener('mousemove', this.onCalendarDrag)
      document.removeEventListener('mouseup', this.endCalendarDrag)
    },
    onCheckShareModalForm() {
      this.$v.share_form.$touch()
      if (this.$v.share_form.$invalid) this.$notify({ type: 'info', title: 'Check that the form fields are filled in correctly' })
      else this.sendShareForm()
    },
    sendShareForm() {
      this.$refs.submit_share.preload = true
      postReservationsSafariDriverSchedule(this.normalizeShareForm())
        .finally(() => { this.$refs.submit_share.preload = false })
        .then(() => { this.$modal.hide('share-popup'); this.$notify({ type: 'success', title: 'Schedule sent' }) })
    },
    normalizeShareForm() {
      return { from_date: this.share_form.date[0], to_date: this.share_form.date[1], drivers: this.share_form.drivers.map(driver => driver.id) }
    },
    onAddAllDrivers() { this.share_form.drivers = this.safari_driver_options },
    onRemoveAllDrivers() { this.share_form.drivers = [] },
    onClickShareButton() {
      this.$refs.share.preload = true
      getEmployeesList({ position: 'safari_driver', employment_status: 'working' })
        .finally(() => { this.$refs.share.preload = false })
        .then(res => { this.safari_driver_options = res.data; this.share_form.date = [this.filter_dates.start, this.filter_dates.end]; this.$modal.show('share-popup') })
    },
    onClickRefreshButton() { this.fetchItems() },
    fetchItems() {
      const requestId = ++this.calendar_request_id
      this.is_refreshing = !this.is_initial_loading
      this.availability_error = false
      const params = { from_date: this.filter_dates.start, to_date: this.filter_dates.end }
      if (this.switchable || this.isGuideCalendar) {
        const calendarRequest = this.switchable ? getReservationsSafariScheduleCombinedCalendarV2(params) : getReservationsSafariScheduleGuideCalendarV2(params)
        const paidLeavesRequest = getReservationsSafariScheduleGuidePaidLeaves(params)
          .then(res => res.data)
          .catch(() => {
            if (requestId === this.calendar_request_id) {
              this.$notify({ type: 'error', title: 'Paid leave could not be refreshed. Calendar leave data is shown.' })
            }
            return null
          })
        return Promise.all([calendarRequest, paidLeavesRequest])
          .then(([res, paidLeaves]) => {
            if (requestId !== this.calendar_request_id) return
            const guideCalendar = this.switchable ? res.data.guide : res.data
            if (paidLeaves !== null) {
              // Replace the existing paid layer, retaining other leave types.
              guideCalendar.leaves = [
                ...(guideCalendar.leaves || []).filter(leave => leave.leave_type.code !== 'paid_leave'),
                ...paidLeaves
              ]
            }
            if (this.switchable) {
              this.calendar_modes = res.data
              this.applyCalendarMode(this.active_mode)
            } else {
              this.calendar = guideCalendar
              this.availability = {
                vehicle_blocks: [],
                training_blocks: guideCalendar.training_blocks || [],
                leaves: guideCalendar.leaves || [],
              }
            }
          })
          .catch(() => {
            if (requestId === this.calendar_request_id) this.$notify({ type: 'error', title: 'Safari guide calendar could not be loaded' })
          })
          .finally(() => {
            if (requestId !== this.calendar_request_id) return
            this.is_initial_loading = false
            this.is_refreshing = false
            if (this.$refs.refresh) this.$refs.refresh.preload = false
          })
      }
      const availabilityRequest = getReservationsSafariScheduleAvailability(params)
        .then(res => res.data)
        .catch(() => {
          this.availability_error = true
          return { vehicle_blocks: [], training_blocks: [], leaves: [] }
        })
      return Promise.all([
        getReservationsSafariScheduleCalendarV2(params),
        availabilityRequest,
      ])
        .then(([calendar, availability]) => {
          this.calendar = calendar.data
          this.availability = availability
        })
        .catch(() => { this.$notify({ type: 'error', title: 'Safari calendar could not be loaded' }) })
        .finally(() => { this.is_initial_loading = false; this.is_refreshing = false; if (this.$refs.refresh) this.$refs.refresh.preload = false })
    },
    setCalendarMode(mode) {
      if (!this.switchable || mode === this.active_mode || !this.calendar_modes[mode]) return
      const scrollLeft = this.$refs.calendar_scroll ? this.$refs.calendar_scroll.scrollLeft : 0
      this.active_mode = mode
      this.applyCalendarMode(mode)
      this.$nextTick(() => {
        if (this.$refs.calendar_scroll) this.$refs.calendar_scroll.scrollLeft = scrollLeft
      })
    },
    applyCalendarMode(mode) {
      const calendar = this.calendar_modes[mode]
      if (!calendar) return
      this.calendar = calendar
      if (mode === 'guide') {
        this.availability = { vehicle_blocks: [], training_blocks: calendar.training_blocks || [], leaves: calendar.leaves || [] }
      } else {
        this.availability = { vehicle_blocks: calendar.vehicle_blocks || [], training_blocks: [], leaves: [] }
      }
      this.selected_vehicle_ids = []
      this.selected_dates = []
      this.selected_block = null
      this.selected_leave = null
    },
    onIsHiddenPicker() {
      if (this.filter_dates && this.filter_dates.start && this.filter_dates.end) { this.date_picker_error = false; this.fetchItems() } else this.date_picker_error = true
    },
    dayColumn(date) { return this.calendar.days.findIndex(day => day.date === date) + 2 },
    bookingsForVehicle(vehicleId) { return this.calendar.bookings.filter(booking => booking.vehicle_id === vehicleId) },
    isVehicleSelected(vehicleId) { return this.selected_vehicle_ids.includes(vehicleId) },
    isDaySelected(date) { return this.selected_dates.includes(date) },
    toggleVehicle(vehicleId) {
      if (this.isVehicleSelected(vehicleId)) this.selected_vehicle_ids = this.selected_vehicle_ids.filter(id => id !== vehicleId)
      else this.selected_vehicle_ids = [...this.selected_vehicle_ids, vehicleId]
    },
    onDayClick(date) {
      if (this.is_calendar_dragging) return
      if (this.isDaySelected(date)) this.selected_dates = this.selected_dates.filter(item => item !== date)
      else this.selected_dates = [...this.selected_dates, date]
    },
    bookingStyle(booking, vehicleIndex) {
      const start = this.dayColumn(booking.date_start)
      const end = this.dayColumn(booking.date_end)
      const layout = this.calendarItemLayout[booking.id] || { track: 0 }
      return {
        gridColumn: `${start} / ${end + 1}`,
        gridRow: vehicleIndex + 2,
        marginTop: `${2 + (layout.track * 44)}px`,
      }
    },
    blockStyle(block, vehicleIndex) {
      const start = this.dayColumn(block.from_date)
      const end = this.dayColumn(block.to_date)
      const layout = this.calendarItemLayout[block.id] || { track: 0 }
      return {
        gridColumn: `${start} / ${end + 1}`,
        gridRow: vehicleIndex + 2,
        marginTop: `${2 + (layout.track * 44)}px`,
      }
    },
    vehicleRowHeight(vehicleId) {
      const items = [...this.bookingsForVehicle(vehicleId), ...this.availabilityBlocksForVehicle(vehicleId), ...this.leavesForVehicle(vehicleId)]
      const trackCount = items.reduce((maximum, item) => Math.max(maximum, (this.calendarItemLayout[item.id] || {}).track_count || 1), 1)
      return Math.max(48, (trackCount * 44) + 4)
    },
    availabilityBlocksForVehicle(vehicleId) {
      return this.availabilityBlocks.filter(block => block.vehicle_id === vehicleId)
    },
    leavesForVehicle(vehicleId) {
      if (!this.isGuideCalendar) return []
      return this.availability.leaves.filter(leave => leave.vehicle_id === vehicleId)
    },
    isBookingConflict(bookingId) {
      return this.bookingConflictIds.includes(bookingId)
    },
    hasVehicleConflict(vehicleId) {
      return this.bookingsForVehicle(vehicleId).some(booking => this.isBookingConflict(booking.id))
    },
    hasGuideLeaveConflict(booking) {
      return Boolean(booking.guide_leave_conflicts && booking.guide_leave_conflicts.length)
    },
    openBlockModal() {
      const selectedResource = this.block_resource_options[0] || null
      const selectedDate = this.filter_dates.start
      this.block_form = { resource: selectedResource, from_date: selectedDate, to_date: selectedDate, description: '' }
      this.$modal.show('schedule-block')
    },
    createBlock() {
      const form = this.block_form
      if (!form.resource || !form.from_date || !form.to_date) {
        this.$notify({ type: 'info', title: 'Fill in all required block fields' })
        return
      }
      this.$refs.blockSubmit.preload = true
      postReservationsSafariScheduleBlock({
        kind: this.isGuideCalendar ? 'training' : 'maintenance',
        car: this.isCarCalendar ? form.resource.car_id : null,
        employee: this.isGuideCalendar ? form.resource.employee_id : null,
        leave_type: null,
        from_date: form.from_date,
        to_date: form.to_date,
        description: form.description,
      })
        .then(() => this.fetchItems())
        .then(() => { this.$modal.hide('schedule-block'); this.$notify({ type: 'success', title: `${this.availabilityBlockLabel} added` }) })
        .catch(() => {})
        .finally(() => { this.$refs.blockSubmit.preload = false })
    },
    openBlockDetail(block) {
      this.selected_block = block
      this.$modal.show('schedule-block-detail')
    },
    openLeaveDetail(leave) {
      this.selected_leave = leave
      this.$modal.show('guide-leave-detail')
    },
    cancelBlock(event) {
      if (!event) return
      this.$refs.blockCancel.preload = true
      postReservationsSafariScheduleBlockCancel(Number(event.id.split(':')[1]))
        .then(() => this.fetchItems())
        .then(() => { this.$modal.hide('schedule-block-detail'); this.$notify({ type: 'success', title: `${this.availabilityBlockLabel} cancelled` }) })
        .catch(() => {})
        .finally(() => { if (this.$refs.blockCancel) this.$refs.blockCancel.preload = false })
    },
    openAffectedBooking(booking) {
      this.$modal.hide('schedule-block-detail')
      this.onBookingClick(booking)
    },
    bookingStatusClass(booking) {
      if (booking.status.code === 'booked_provisionally') return 'safari-calendar__status--provisional'
      if (booking.status.code === 'booked') {
        if (booking.car_confirmed) return 'safari-calendar__status--confirmed'
        return 'safari-calendar__status--pending'
      }
      return 'safari-calendar__status--request'
    },
    allMarkers(markers) {
      const result = []
      if (markers.spoken_language) result.push({
        key: 'language',
        label: markers.spoken_language.code.toUpperCase(),
        flag_country_code: this.languageFlagCode(markers.spoken_language),
        detailValue: this.languageName(markers.spoken_language),
        tooltip: `Group language: ${this.languageName(markers.spoken_language)}`,
      })
      if (markers.starts_in_serengeti || markers.finishes_in_serengeti) {
        const starts = markers.starts_in_serengeti
        const finishes = markers.finishes_in_serengeti
        const flyIn = `Fly-in to ${markers.fly_in_location ? this.locationName(markers.fly_in_location) : 'location'}`
        const flyOut = `Fly-out from ${markers.fly_out_location ? this.locationName(markers.fly_out_location) : 'location'}`
        if (starts) result.push({ key: 'serengeti_arrival', label: '🛬', detailValue: flyIn })
        if (finishes) result.push({ key: 'serengeti_departure', label: '🛫', detailValue: flyOut })
      }
      if (markers.has_children) {
        const ages = markers.children_ages || []
        const childrenText = `${markers.children_count} child${markers.children_count === 1 ? '' : 'ren'}`
        const ageText = ages.length ? `. ${ages.length === 1 ? 'Age' : 'Ages'}: ${ages.map(age => `${age}yo`).join(', ')}` : ''
        result.push({
          key: 'children',
          label: '🧸',
          detailValue: childrenText,
          tooltip: `${childrenText}${ageText}`,
        })
      }
      if (markers.has_seniors_over_60) result.push({ key: 'seniors', label: '60+', detailValue: `${markers.seniors_over_60_count} guest${markers.seniors_over_60_count === 1 ? '' : 's'} aged 60+` })
      if (markers.honeymooners) result.push({ key: 'honeymooners', label: '💍', detailValue: 'Honeymooners' })
      if (markers.birdwatchers) result.push({ key: 'birdwatchers', label: '🦩', detailValue: 'Birdwatching requested' })
      if (markers.special_assistance) result.push({ key: 'assistance', label: '♿', detailValue: 'Special assistance required' })
      return result
    },
    compactMarkers(booking) {
      const markers = this.allMarkers(booking.markers)
      const days = moment(booking.date_end).diff(moment(booking.date_start), 'days') + 1
      const limit = days === 1 ? 1 : days === 2 ? 3 : 6
      const visible = markers.slice(0, limit)
      if (markers.length > limit) {
        const hiddenCount = markers.length - limit
        visible.push({ key: 'more', label: `+${hiddenCount}`, detailValue: `${hiddenCount} more group marker${hiddenCount === 1 ? '' : 's'}` })
      }
      return visible
    },
    languageName(language) {
      const names = { fr: 'French', de: 'German', it: 'Italian', es: 'Spanish' }
      return language.name || names[language.code.toLowerCase()] || language.code.toUpperCase()
    },
    guideLanguageTooltip(language) {
      const level = language.level_name || language.level_code
      return `Guide speaks ${this.languageName(language)}${level ? ` (${level.toUpperCase()})` : ''}`
    },
    languageFlagCode(language) {
      const countryCode = language.flag_country_code || language.language?.flag_country_code || language.code
      return this.languageFlagCountry[countryCode] || this.languageFlagCountry[countryCode && countryCode.toUpperCase()]
    },
    driverName(driver) { return driver.display_name || driver.full_name },
    resourcePrimaryLabel(vehicle) {
      if (!this.switchable || this.isGuideCalendar) return vehicle.name
      return vehicle.name.replace(/\s+\|\s+\d+$/, '')
    },
    bookedDaysLabel(days) { return `${days} ${days === 1 ? 'day' : 'days'}` },
    hasGuideSkills(driver) { return driver.strong_in_birds || driver.great_with_kids || driver.influencer_experienced },
    hasGuideTags(driver) { return driver.languages.length || this.hasGuideSkills(driver) },
    locationName(location) { return location ? (location.short_name || location.name) : 'Not specified' },
    flightLocationName(location) {
      return this.locationName(location)
        .replace(/\s+(international\s+)?(airport|airstrip)$/i, '')
        .replace(/^serengeti\s+/i, '')
    },
    humanizeCode(value) { return value ? value.replace(/_/g, ' ').replace(/^./, letter => letter.toUpperCase()) : 'Safari' },
    birthdayTooltip(client) {
      return `${client.first_name} turns ${client.turning_age} on ${this.formatDate(client.birthday_date)}`
    },
    onBookingClick(booking) {
      this.resetBookingDetails()
      this.is_booking_loading = true
      this.$modal.show('booking-details')
      getReservationsSafariScheduleCalendarBooking(booking.assignment_type, booking.assignment_id)
        .then(res => { this.detail = res.data })
        .catch(() => { this.booking_detail_error = true })
        .finally(() => { this.is_booking_loading = false })
    },
    resetBookingDetails() { this.detail = {}; this.booking_detail_error = false; this.is_booking_loading = false },
    formatDate(date) { return moment(date).format('D MMM YYYY') },
    duration(dates) { return moment(dates.tour_end).diff(moment(dates.tour_start), 'days') + 1 },
    vehicleLabel(vehicle) {
      if (vehicle.plate_number && vehicle.name.includes(vehicle.plate_number)) return vehicle.name
      return [vehicle.name, vehicle.plate_number].filter(Boolean).join(' / ')
    },
    openReservation() {
      const route = this.$router.resolve(this.detail.links.reservation)
      const reservationWindow = window.open(route.href, '_blank', 'noopener')
      if (reservationWindow) reservationWindow.opener = null
    },
  }
}
</script>

<style scoped>
.safari-calendar__mode-switch { display: inline-flex; padding: 3px; border: 1px solid #d6d9dc; border-radius: 7px; background: #f4f6f7; }
.safari-calendar__mode-switch button { min-width: 94px; min-height: 34px; padding: 6px 10px; border: 0; border-radius: 5px; display: inline-flex; align-items: center; justify-content: center; gap: 7px; background: transparent; color: #687680; cursor: pointer; font: inherit; font-size: 14px; font-weight: 600; }
.safari-calendar__mode-switch button:focus-visible { outline: 2px solid rgba(119, 176, 23, .35); outline-offset: 1px; }
.safari-calendar__mode-switch .safari-calendar__mode-button--active { background: #77b017; color: #fff; box-shadow: 0 1px 3px rgba(46, 64, 30, .2); }
.safari-calendar__mode-conflict-count { min-width: 22px; height: 22px; padding: 0 5px; border: 2px solid rgba(255, 255, 255, .9); border-radius: 11px; box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; background: #d84c4c; color: #fff; box-shadow: 0 1px 3px rgba(98, 31, 31, .2); font-size: 11px; font-weight: 800; line-height: 1; }
.safari-calendar__legend { position: relative; margin-left: auto; color: #20272c; font-size: 14px; }
.safari-calendar__legend summary { list-style: none; }.safari-calendar__legend summary::-webkit-details-marker { display: none; }
.safari-calendar__legend-button { min-height: 40px; padding: 8px 13px; border: 1px solid #d6d9dc; border-radius: 7px; box-sizing: border-box; display: flex; align-items: center; gap: 9px; background: #fff; color: #273037; cursor: pointer; font-size: 14px; font-weight: 600; white-space: nowrap; }.safari-calendar__legend-button img { width: 18px; height: 14px; }.safari-calendar__legend-button > i { width: 8px; height: 8px; margin-left: 2px; border-right: 2px solid #59646c; border-bottom: 2px solid #59646c; transform: translateY(-2px) rotate(45deg); transition: transform .15s ease; }.safari-calendar__legend[open] .safari-calendar__legend-button > i { transform: translateY(2px) rotate(225deg); }
.safari-calendar__legend-panel { position: absolute; top: calc(100% + 8px); right: 0; z-index: 20; width: 310px; padding: 18px 20px; border: 1px solid #d6d9dc; border-radius: 7px; box-sizing: border-box; background: #fff; box-shadow: 0 10px 25px rgba(31, 43, 51, .14); }.safari-calendar__legend-panel section + section { margin-top: 16px; padding-top: 16px; border-top: 1px solid #e3e6e8; }.safari-calendar__legend-panel section > strong { display: block; margin-bottom: 10px; color: #77828d; font-size: 12px; font-weight: 600; text-transform: uppercase; }.safari-calendar__legend-panel ul { margin: 0; padding: 0; list-style: none; }.safari-calendar__legend-panel li { min-height: 27px; display: flex; align-items: center; gap: 10px; line-height: 1.25; }.safari-calendar__legend-panel li > span:first-child { min-width: 25px; text-align: center; }
.safari-calendar__legend-status { width: 30px; height: 22px; flex: 0 0 30px; border-left: 5px solid var(--status-stripe); border-radius: 3px; box-sizing: border-box; background: var(--status-background); }
.safari-calendar__legend-availability { width: 30px; height: 22px; flex: 0 0 30px; border-left: 5px solid #7840aa; border-radius: 3px; box-sizing: border-box; }.safari-calendar__legend-availability--maintenance { background: #eee3f8; }.safari-calendar__legend-availability--training { border-left-color: #4b5563; background: #eef0f2; }.safari-calendar__legend-conflict { width: 30px; height: 22px; flex: 0 0 30px; display: flex; align-items: center; justify-content: center; }.safari-calendar__legend-conflict img { width: 18px; height: 18px; display: block; }.safari-calendar__legend-guide-leave { width: 30px; height: 22px; flex: 0 0 30px; border-left: 5px solid #cf4747; border-radius: 3px; box-sizing: border-box; background: #fde8e8; }.safari-calendar__legend-lock { width: 30px; height: 22px; flex: 0 0 30px; border: 2px solid #15191c; border-radius: 3px; box-sizing: border-box; background: #fff; }
.safari-calendar__legend-senior { min-width: 34px !important; padding: 2px 4px; border-radius: 3px; background: #4d5357; color: #fff; box-sizing: border-box; font-size: 12px; font-weight: 700; }
.safari-calendar__scroll { width: calc(100vw - 315px); max-width: calc(100vw - 315px); min-width: 0; max-height: calc(100vh - 220px); overflow: auto; padding-bottom: 10px; scrollbar-color: #77b017 transparent; scrollbar-width: thin; }
.safari-calendar__scroll--dragging .safari-calendar__day { cursor: grabbing; }
.safari-calendar__scroll::-webkit-scrollbar { height: 7px; }.safari-calendar__scroll::-webkit-scrollbar-thumb { background: rgba(119, 176, 23, .55); border-radius: 4px; }
.safari-calendar__grid { display: grid; min-width: max-content; border-top: 1px solid #e4e9ed; border-left: 1px solid #e4e9ed; }
.safari-calendar__corner, .safari-calendar__day, .safari-calendar__vehicle, .safari-calendar__cell { border-right: 1px solid #e4e9ed; border-bottom: 1px solid #e4e9ed; }
.safari-calendar__corner, .safari-calendar__day { min-height: 46px; padding: 7px; box-sizing: border-box; display: flex; flex-direction: column; justify-content: center; color: #74808a; font-size: 14px; }
.safari-calendar__corner { position: sticky; top: 0; left: 0; z-index: 6; background: #fff; box-shadow: 2px 0 4px rgba(45, 60, 70, .06); }.safari-calendar__day { position: sticky; top: 0; z-index: 4; align-items: center; cursor: grab; background: #fff; }.safari-calendar__day--today { background: #f4f9ed; color: #5b8d13; }.safari-calendar__day--selected { background: #fff5c7; color: #5c5229; }.safari-calendar__day small { margin-top: 2px; }
.safari-calendar__vehicle { grid-column: 1; min-height: 48px; padding: 5px 8px 5px 10px; box-sizing: border-box; display: flex; flex-direction: column; justify-content: center; gap: 3px; position: sticky; left: 0; background: #fff; z-index: 5; box-shadow: 2px 0 4px rgba(45, 60, 70, .06); cursor: pointer; }.safari-calendar__vehicle--conflict { background: #fff1f1; box-shadow: inset 3px 0 0 #dc6b6b, 2px 0 4px rgba(45, 60, 70, .06); }.safari-calendar__vehicle--selected { background: #fff5c7; }
.safari-calendar__vehicle-name, .safari-calendar__driver { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.safari-calendar__vehicle-name { width: 100%; flex-shrink: 0; color: #35424c; font-size: 14px; }.safari-calendar__guide-row { min-width: 0; width: 100%; flex-shrink: 0; display: flex; align-items: center; gap: 5px; }.safari-calendar__driver { min-width: 0; flex: 1; color: #5e6d77; font-size: 14px; }
.safari-calendar__guide-tags { margin-left: auto; display: flex; align-items: center; gap: 4px; flex-shrink: 0; }.safari-calendar__guide-tags span { color: #405260; font-size: 13px; line-height: 1; }
.safari-calendar__cell { min-height: 48px; box-sizing: border-box; }.safari-calendar__cell--conflict { background: #fff8f8; }.safari-calendar__cell--selected { background: #fff9dc; }.safari-calendar__booking { align-self: start; height: 44px; min-width: 0; margin: 2px; padding: 4px 5px 4px 7px; border: 0; border-left: 5px solid var(--status-stripe); border-radius: 2px; box-sizing: border-box; display: flex; flex-direction: column; align-items: flex-start; justify-content: center; gap: 1px; background: var(--status-background); color: var(--status-text); cursor: pointer; overflow: hidden; z-index: 2; font: inherit; font-size: 14px; font-weight: 600; text-align: left; }.safari-calendar__booking--locked { box-shadow: inset 0 0 0 2px #15191c; }.safari-calendar__status--request { --status-background: rgba(226, 224, 253, .58); --status-stripe: #7f77dd; --status-text: #26215c; }.safari-calendar__status--provisional { --status-background: rgba(212, 231, 248, .58); --status-stripe: #378add; --status-text: #0c447c; }.safari-calendar__status--confirmed { --status-background: rgba(214, 229, 195, .58); --status-stripe: #5f8a3b; --status-text: #27500a; }.safari-calendar__status--pending { --status-background: rgba(246, 226, 191, .58); --status-stripe: #ba7517; --status-text: #633806; }.safari-calendar__booking-title { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.safari-calendar__markers { max-width: 100%; display: flex; align-items: center; gap: 5px; white-space: nowrap; }.safari-calendar__markers > span { color: inherit; line-height: 1; }.safari-calendar__marker--senior { font-weight: 800; }.safari-calendar__marker--more { font-weight: 800; }
.safari-calendar__loading { padding: 8px 0; color: #71808a; }
.safari-calendar__availability-warning { margin: 8px 0; padding: 9px 12px; border: 1px solid #e0b25b; border-radius: 6px; background: #fff9ec; color: #714814; }
.safari-calendar__booking-warning { width: 18px; height: 18px; flex: 0 0 18px; display: inline-flex; align-items: center; justify-content: center; }.safari-calendar__booking-warning img { width: 16px; height: 16px; display: block; }
.safari-calendar__block { align-self: start; height: 44px; min-width: 0; margin: 2px; padding: 4px 7px; z-index: 2; display: flex; align-items: center; justify-content: center; gap: 6px; border: 0; border-left: 5px solid #7840aa; border-radius: 2px; box-sizing: border-box; overflow: hidden; background: #eee3f8; color: #4f276b; cursor: pointer; }.safari-calendar__block-icon { flex: 0 0 auto; font-size: 18px; line-height: 1; }.safari-calendar__block-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; font-weight: 600; }
.safari-calendar__block--training { border-left-color: #4b5563; background: #eef0f2; color: #30343b; }
.safari-calendar__leave { align-self: start; height: 44px; min-width: 0; margin: 2px; padding: 4px 7px; z-index: 2; display: flex; align-items: center; border: 0; border-left: 5px solid #cf4747; border-radius: 2px; box-sizing: border-box; overflow: hidden; background: #fde8e8; color: #8f3030; cursor: pointer; font: inherit; font-size: 14px; font-weight: 600; }
.safari-block-detail { display: flex; flex-direction: column; gap: 10px; }.safari-block-detail p { margin: 4px 0; }.safari-block-detail__cause { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px; border: 1px solid #dfe5e8; border-radius: 6px; }.safari-block-detail__cause > div { display: flex; flex-direction: column; gap: 3px; }.safari-block-detail__bookings { display: flex; flex-direction: column; gap: 5px; padding-top: 8px; border-top: 1px solid #e2e7ea; }.safari-block-detail__bookings button { padding: 7px 0; border: 0; background: transparent; color: #568c15; text-align: left; cursor: pointer; }
.safari-booking-modal__body { display: grid; gap: 25px; font-size: 14px; }.safari-booking-modal__body--with-itinerary { grid-template-columns: minmax(460px, 1.2fr) minmax(330px, .8fr); }.safari-booking-modal__heading { display: flex; justify-content: space-between; gap: 12px; }.safari-booking-modal__heading span, .safari-booking-modal__section > span, .safari-booking-modal__itinerary > span, .safari-booking-modal__facts span, .safari-booking-modal__dates span { display: block; color: #71808a; text-transform: uppercase; font-size: 13px; font-weight: 600; }.safari-booking-modal__heading span, .safari-booking-modal__facts span, .safari-booking-modal__dates div span, .safari-booking-modal__dates strong { white-space: nowrap; }.safari-booking-modal__heading h2 { margin: 5px 0 18px; }.safari-booking-modal__heading b { height: min-content; padding: 6px 10px; border-radius: 3px; background: #e5f3db; color: #4c7b22; font-size: 14px; white-space: nowrap; }.safari-booking-modal__dates, .safari-booking-modal__facts { display: flex; align-items: center; gap: 18px; margin-bottom: 12px; padding: 12px; border: 1px solid #dce5ea; border-radius: 6px; }.safari-booking-modal__dates { background: #f4f8f1; }.safari-booking-modal__dates > span { text-transform: none; font-weight: 400; }.safari-booking-modal__dates b { margin-left: auto; flex-shrink: 0; padding: 6px 9px; background: #edf2f5; color: #5f7180; border-radius: 4px; }.safari-booking-modal__facts > div { min-width: 0; flex: 1; }.safari-booking-modal__facts--route { align-items: flex-start; background: #fafcfd; }.safari-booking-modal__section { margin-top: 16px; }.safari-booking-modal__section > span, .safari-booking-modal__itinerary > span { margin-bottom: 8px; }.safari-booking-modal__section p { margin: 0; color: #687985; }.safari-booking-modal__chips { display: flex; flex-wrap: wrap; gap: 7px; }.safari-booking-modal__chips b { padding: 5px 8px; border-radius: 4px; background: #edf4ff; color: #3967a2; font-size: 14px; }.safari-booking-modal__chips--plain b { padding: 0; background: transparent; color: #405260; }.safari-booking-modal__markers { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }.safari-booking-modal__markers div { display: flex; justify-content: space-between; gap: 8px; padding: 9px; border: 1px solid #dce5ea; border-radius: 5px; color: #5d6c76; }.safari-booking-modal__markers strong { color: #2e3940; }.safari-booking-modal__clients { width: 100%; border-collapse: collapse; font-size: 13px; }.safari-booking-modal__clients th, .safari-booking-modal__clients td { padding: 7px 8px; border-bottom: 1px solid #e6ecef; text-align: left; }.safari-booking-modal__clients th { color: #71808a; font-weight: 600; }.safari-booking-modal__itinerary { min-width: 0; padding-left: 24px; border-left: 1px solid #e2e9ed; }.safari-booking-modal__itinerary div { padding: 8px 0; border-bottom: 1px solid #edf1f3; }.safari-booking-modal__itinerary strong { display: block; color: #586b77; font-size: 14px; }.safari-booking-modal__itinerary small { display: block; margin-top: 3px; color: #82909a; font-size: 12px; }
.safari-booking-modal__guide-leave { margin: 12px 0; padding: 12px; border: 1px solid #d65b5b; border-radius: 6px; background: #fff1f1; color: #923535; }.safari-booking-modal__guide-leave > strong { display: block; margin-bottom: 8px; }.safari-booking-modal__guide-leave > div { display: flex; flex-direction: column; gap: 3px; }.safari-booking-modal__guide-leave > div + div { margin-top: 8px; padding-top: 8px; border-top: 1px solid #efcaca; }.safari-booking-modal__guide-leave span, .safari-booking-modal__guide-leave b { font-size: 13px; }
.safari-booking-modal__locks > div { display: flex; flex-wrap: wrap; gap: 7px; }.safari-booking-modal__locks b { padding: 6px 9px; border: 2px solid #15191c; border-radius: 4px; color: #20272c; cursor: help; font-size: 13px; }
.safari-booking-modal__loading { min-height: 170px; display: flex; align-items: center; justify-content: center; }
@media (max-width: 900px) { .safari-calendar__scroll { width: calc(100vw - 20px); max-width: calc(100vw - 20px); }.safari-calendar__legend-panel { width: calc(100vw - 20px); max-width: 310px; }.safari-booking-modal__body--with-itinerary { grid-template-columns: minmax(0, 1fr); }.safari-booking-modal__itinerary { padding: 20px 0 0; border-left: 0; border-top: 1px solid #e2e9ed; } }
</style>
